import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type { DepartmentCreateSchema, DepartmentUpdateSchema } from './department-mutation-schema';
import type { DepartmentQueryAllSchema, DepartmentQuerySchema } from './department-query-schema';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['DEPARTMENTS'] });
};

export const useGetDepartmentsMutation: UseControlledMutation<DepartmentQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['DEPARTMENTS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<DepartmentQueryAllSchema>({
            endpoint: '/api/v1/departments',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetDepartments(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['DEPARTMENTS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<DepartmentQueryAllSchema>({
        endpoint: '/api/v1/departments',
        params: `${apiParamStr}&include=parent`,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, DepartmentCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/departments/`,
        payload,
      }),
  });

export function useGetDepartment(id: string) {
  return useSuspenseQuery({
    queryKey: ['DEPARTMENT', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: DepartmentQuerySchema }>({
        endpoint: `/api/v1/departments/${id}`,
        params: { include: 'parent' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: DepartmentQuerySchema }, ApiError, Partial<DepartmentUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/departments/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['DEPARTMENT_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/departments/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
